import { ZoneType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { addZoneType } from "@/lib/api/zone"
import { useToast } from "@/hooks/use-toast"

import { Icons } from "../icons"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  zone_name: z.string().min(1, "Zone name is required"),
  zone_code: z.coerce.number().min(1, "Zone code is required"),
  meter_reading_schedule: z.coerce
    .number()
    .min(1, "Meter reading schedule is required"),
  zone_description: z.string().min(1, "Zone description is required"),
  zone_capacity: z.string().min(1, "Zone capacity is required"),
  zone_status: z.string().min(1, "Zone status is required"),
})

export default function ZonesForm({
  setShowAddZoneTypeModal,
}: {
  setShowAddZoneTypeModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      zone_name: "",
      zone_code: 0,
      meter_reading_schedule: 0,
      zone_description: "",
      zone_capacity: "",
      zone_status: "",
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ZoneType) => {
      await addZoneType(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zonesDetails"] })
      toast({
        description: "Zone Type added successfully",
      })
      setShowAddZoneTypeModal(false)
    },
    onError: async (error: any) => {
      const response = error.response
      let errorMessage = "Please try again."

      if (response && response.status === 400) {
        const errorData = await response.json()
        errorMessage = errorData.message || JSON.stringify(errorData)
      }
      toast({
        title: "Something went wrong!",
        description: errorMessage,
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    mutation.mutate(values as ZoneType)
  }
  return (
    <div className="px-2">
      <DropdownMenuSeparator className="mt-4 h-[1px] bg-muted" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            {/* Zone Name and Zone Code */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="zone_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zone_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Code <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            {/* Meter Reading Schedule */}
            <FormField
              control={form.control}
              name="meter_reading_schedule"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Meter Reading Schedule{" "}
                    <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            {/* Zone Description */}
            <FormField
              control={form.control}
              name="zone_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Zone Description <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea className="resize-none" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            {/* Zone Capacity and Zone Status */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="zone_capacity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Capacity <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="zone_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Zone Status <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </Card>
          <div className="flex flex-row justify-end gap-2">
            <Button
              className="w-28"
              variant="outline"
              type="button"
              onClick={() => {
                setShowAddZoneTypeModal(false)
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Add
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
